import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBookOpen, FiSearch, FiCode, FiFilter } = FiIcons;

const HelpPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  const sections = [
    {
      icon: FiSearch,
      title: "Operadores booleanos",
      content: [
        {
          operator: "AND (+)",
          description: "Busca documentos que contengan ambos términos",
          examples: [
            "inteligencia AND artificial",
            "inteligencia + artificial"
          ]
        },
        {
          operator: "OR (|)",
          description: "Busca documentos que contengan cualquiera de los términos",
          examples: [
            "entrenamiento OR aprendizaje",
            "entrenamiento | aprendizaje"
          ]
        },
        {
          operator: "NOT (-)",
          description: "Excluye documentos que contengan el término",
          examples: [
            "artificial -intelligence (excluye 'intelligence')"
          ]
        }
      ]
    },
    {
      icon: FiCode,
      title: "Truncadores y comodines",
      content: [
        {
          operator: "* (Asterisco)",
          description: "Busca todas las variaciones de una raíz",
          examples: ["educ* encuentra: educación, education, educador, educativo"]
        },
        {
          operator: "% (Porcentaje)",
          description: "Comodín alternativo para truncar",
          examples: ["psicolog% encuentra: psicología, psychology, psicólogo"]
        },
        {
          operator: "? (Interrogación)",
          description: "Reemplaza un solo carácter",
          examples: ["wom?n encuentra: woman, women"]
        }
      ]
    },
    {
      icon: FiBookOpen,
      title: "Operadores de proximidad",
      content: [
        {
          operator: "NEAR",
          description: "Términos cercanos entre sí",
          examples: ["artificial NEAR intelligence"]
        },
        {
          operator: "SAME",
          description: "Términos en el mismo campo o párrafo",
          examples: ["machine SAME learning"]
        },
        {
          operator: "WITH",
          description: "Términos dentro de una distancia específica",
          examples: ["neural WITH network"]
        }
      ]
    },
    {
      icon: FiFilter,
      title: "Búsqueda avanzada",
      content: [
        {
          operator: "\" \" (Comillas)",
          description: "Busca la frase exacta",
          examples: ["\"inteligencia artificial\" busca la frase completa"]
        },
        {
          operator: "( ) (Paréntesis)",
          description: "Agrupa operadores para búsquedas complejas",
          examples: ["(machine OR deep) AND learning"]
        },
        {
          operator: "Campos específicos",
          description: "Busca en campos específicos del documento",
          examples: ["title:\"machine learning\" author:\"John Smith\""]
        }
      ]
    }
  ];

  const tips = [
    "Usa comillas para frases exactas: \"machine learning\"",
    "Combina operadores: (AI OR \"artificial intelligence\") AND ethics",
    "Utiliza truncadores para ampliar resultados: comput* incluye computer, computing, computational",
    "Los paréntesis te ayudan a crear búsquedas complejas y precisas",
    "Experimenta con diferentes combinaciones para obtener mejores resultados"
  ];

  // Todas las fuentes académicas organizadas alfabéticamente
  const academicSources = [
    "Academia.edu",
    "arXiv",
    "BASE",
    "BioMed Central",
    "bioRxiv",
    "Cambridge Core",
    "Connected Papers",
    "Consensus",
    "CORE",
    "Crossref",
    "Dialnet",
    "Dimensions",
    "DOAB",
    "DOAJ",
    "Elicit",
    "ERIC",
    "Europe PMC",
    "Google Scholar",
    "IEEE Xplore",
    "Inciteful",
    "JSTOR",
    "JURN",
    "La Referencia",
    "MDPI",
    "medRxiv",
    "Open Knowledge Maps",
    "OpenAlex",
    "ORKG Ask",
    "Oxford Academic",
    "Papers with Code",
    "Perplexity AI",
    "PubMed",
    "Redalyc",
    "RefSeek",
    "Research Rabbit",
    "ResearchGate",
    "SAGE Journals",
    "SciELO",
    "Science Research",
    "ScienceDirect",
    "ScienceOpen",
    "SciSpace",
    "Scite",
    "Scopus",
    "Semantic Scholar",
    "Springer Link",
    "Taylor & Francis Online",
    "TESEO",
    "The Lens",
    "Undermind AI",
    "Wiley Online Library",
    "zbMATH"
  ].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Guía de búsqueda académica
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Aprende a utilizar operadores avanzados para búsquedas más precisas y efectivas
            </p>
          </motion.div>

          {/* Sections */}
          <div className="space-y-12">
            {sections.map((section, index) => (
              <motion.section
                key={index}
                variants={itemVariants}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-lg">
                    <SafeIcon icon={section.icon} className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-4">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex} className="border-l-4 border-indigo-500 pl-6 py-2">
                      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                        {item.operator}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 mb-2">
                        {item.description}
                      </p>
                      {item.examples && (
                        <div className="space-y-1">
                          {item.examples.map((example, exampleIndex) => (
                            <code
                              key={exampleIndex}
                              className="block bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded text-sm text-gray-800 dark:text-gray-200"
                            >
                              {example}
                            </code>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>

          {/* Tips Section */}
          <motion.section
            variants={itemVariants}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl shadow-lg p-8 text-white mt-12"
          >
            <h2 className="text-2xl font-bold mb-6">Consejos útiles</h2>
            <ul className="space-y-3">
              {tips.map((tip, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-sm font-bold">
                    {index + 1}
                  </span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </motion.section>

          {/* Sources Section */}
          <motion.section
            variants={itemVariants}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 mt-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Fuentes académicas disponibles ({academicSources.length})
            </h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
              {academicSources.map((source, index) => (
                <div
                  key={index}
                  className="bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded-lg text-center text-sm text-gray-700 dark:text-gray-300"
                >
                  {source}
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 rounded-lg">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Nota:</strong> Todas las fuentes están organizadas alfabéticamente y proporcionan acceso directo a contenido académico verificado. El sistema integra tanto conexiones directas como enlaces externos para maximizar la cobertura de búsqueda.
              </p>
            </div>
          </motion.section>
        </motion.div>
      </div>
    </div>
  );
};

export default HelpPage;